import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void
): void;

function useEventListener<
  WE extends keyof WindowEventMap,
  HE extends keyof HTMLElementEventMap,
  T extends HTMLElement | void = void
>(
  eventName: WE | HE,
  handler: (
    event: WindowEventMap[WE] | HTMLElementEventMap[HE] | Event
  ) => void,
  active?: boolean,
  element?: RefObject<T>
): void;

function useEventListener<
  WE extends keyof WindowEventMap,
  HE extends keyof HTMLElementEventMap,
  T extends HTMLElement | void = void
>(
  eventName: WE | HE,
  handler: (
    event: WindowEventMap[WE] | HTMLElementEventMap[HE] | Event
  ) => void,
  active?: boolean,
  element?: RefObject<T>
) {
  const savedHandler = useRef(handler);

  useLayoutEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!active) return;

    const targetElement: T | Window = element?.current || window;

    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    const eventListener: typeof handler = (event) =>
      savedHandler.current(event);

    targetElement.addEventListener(eventName, eventListener);

    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, active]);
}

export default useEventListener;
